import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Typography,
} from '@mui/material';
import { User, CreateUserRequest, UpdateUserRequest } from '../../services/userService';
import { publisherService, Publisher } from '../../services/publisherService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types';
import { useQuery } from '@tanstack/react-query';
import AdminPermissionsEditor from './AdminPermissionsEditor';
import { AdminPermissionKey, getDefaultAdminPermissions } from '../../constants/adminPermissions';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (userData: CreateUserRequest | UpdateUserRequest) => void;
  user?: User;
  loading?: boolean;
  onOpenClientAssignments?: () => void;
}

const UserForm: React.FC<UserFormProps> = ({
  open,
  onClose,
  onSubmit,
  user,
  loading = false,
  onOpenClientAssignments,
}) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'ADMIN' as 'ADMIN' | 'SUPER_ADMIN' | 'PUBLISHER' | 'CLIENT' | 'AGENCY',
    isActive: true,
    publisherId: '',
    clientId: '',
  });
  const [adminPermissions, setAdminPermissions] = useState<AdminPermissionKey[]>(
    getDefaultAdminPermissions(),
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch publishers for assignment (only when creating Publisher users)
  const { data: publishersData } = useQuery({
    queryKey: ['publishers-for-assignment'],
    queryFn: () => publisherService.getPublishers({ limit: 1000 }),
    enabled: formData.role === 'PUBLISHER' && open,
  });

  // Fetch clients for CLIENT / AGENCY user connection
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-assignment'],
    queryFn: () => clientService.getClients({ page: 1, limit: 1000 }),
    enabled: (formData.role === 'CLIENT' || formData.role === 'AGENCY') && open,
  });

  const availablePublishers = publishersData?.data?.filter((p: Publisher) => !p.userId) || [];
  const availableClients = clientsData?.data || [];
  const availableAgencyClients = availableClients.filter(
    (c: Client) => !c.agencyId || (user && c.agencyId === user.id),
  );
  const usesClientConnect = formData.role === 'CLIENT' || formData.role === 'AGENCY';
  const linkedClientSelected = usesClientConnect && !!formData.clientId;

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        isActive: user.isActive,
        publisherId: (user as any).publisherId || '',
        clientId:
          user.role === 'AGENCY'
            ? user.agencyClientId || ''
            : user.clientId || '',
      });
      setAdminPermissions(user.adminPermissions ?? getDefaultAdminPermissions());
    } else {
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        role: 'ADMIN',
        isActive: true,
        publisherId: '',
        clientId: '',
      });
      setAdminPermissions(getDefaultAdminPermissions());
    }
    setErrors({});
  }, [user, open]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'role' && value === 'AGENCY' && prev.role === 'CLIENT') {
        next.clientId = '';
      }
      if (field === 'role' && value !== 'CLIENT' && value !== 'AGENCY') {
        next.clientId = '';
      }
      return next;
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Auto-populate form when publisher is selected
  useEffect(() => {
    if (formData.publisherId && availablePublishers.length > 0) {
      const selectedPublisher = availablePublishers.find((p: Publisher) => p.id === formData.publisherId);
      if (selectedPublisher) {
        setFormData(prev => ({
          ...prev,
          email: selectedPublisher.email || '',
          firstName: selectedPublisher.publisherName.split(' ')[0] || '',
          lastName: selectedPublisher.publisherName.split(' ').slice(1).join(' ') || '',
        }));
      }
    }
  }, [formData.publisherId, availablePublishers]);

  // Auto-populate form when client is selected
  useEffect(() => {
    if (formData.clientId && availableClients.length > 0) {
      const selectedClient = availableClients.find((c: Client) => c.id === formData.clientId);
      if (selectedClient) {
        setFormData(prev => ({
          ...prev,
          email: selectedClient.email || '',
          firstName: selectedClient.name.split(' ')[0] || '',
          lastName: selectedClient.name.split(' ').slice(1).join(' ') || '',
        }));
      }
    }
  }, [formData.clientId, availableClients]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name is optional, no validation needed

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!user && !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (!user && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (user) {
      // Update user - don't include email and password
      const updateData: UpdateUserRequest = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        isActive: formData.isActive,
        ...(formData.role === 'ADMIN' && { adminPermissions }),
        ...(formData.role === 'PUBLISHER' && formData.publisherId && { publisherId: formData.publisherId }),
        ...((formData.role === 'CLIENT' || formData.role === 'AGENCY') &&
          formData.clientId && { clientId: formData.clientId }),
      };
      onSubmit(updateData);
    } else {
      // Create user - include all fields
      const createData: CreateUserRequest = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        ...(formData.role === 'ADMIN' && { adminPermissions }),
        ...(formData.role === 'PUBLISHER' && formData.publisherId && { publisherId: formData.publisherId }),
        ...((formData.role === 'CLIENT' || formData.role === 'AGENCY') &&
          formData.clientId && { clientId: formData.clientId }),
      };
      onSubmit(createData);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {user ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  disabled={loading || (formData.role === 'PUBLISHER' && !!formData.publisherId) || linkedClientSelected}
                  InputProps={{
                    readOnly: (formData.role === 'PUBLISHER' && !!formData.publisherId) || linkedClientSelected,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name (Optional)"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName || (formData.role === 'PUBLISHER' && formData.publisherId ? 'You can edit this field if needed' : '') || (linkedClientSelected ? 'You can edit this field if needed' : '')}
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={loading || !!user || (formData.role === 'PUBLISHER' && !!formData.publisherId) || linkedClientSelected}
                  InputProps={{
                    readOnly: !!user || (formData.role === 'PUBLISHER' && !!formData.publisherId) || linkedClientSelected,
                  }}
                />
              </Grid>

              {!user && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    error={!!errors.password}
                    helperText={errors.password}
                    disabled={loading}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.role}
                    label="Role"
                    onChange={(e) => handleChange('role', e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="ADMIN">Admin</MenuItem>
                    <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
                    <MenuItem value="AGENCY">Agency</MenuItem>
                    <MenuItem value="PUBLISHER">Publisher</MenuItem>
                    <MenuItem value="CLIENT">Client</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.role === 'PUBLISHER' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Assign to Publisher</InputLabel>
                    <Select
                      value={formData.publisherId}
                      label="Assign to Publisher"
                      onChange={(e) => handleChange('publisherId', e.target.value)}
                      disabled={loading}
                    >
                      <MenuItem value="">Select Publisher</MenuItem>
                      {availablePublishers.map((publisher: Publisher) => (
                        <MenuItem key={publisher.id} value={publisher.id}>
                          {publisher.email ? 
                            `${publisher.publisherName} (${publisher.email})` : 
                            publisher.publisherName
                          }
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {availablePublishers.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      No publishers available for assignment. All publishers already have user accounts.
                    </Typography>
                  )}
                  {formData.publisherId && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                      ✓ First name and email auto-populated from selected publisher. Last name is editable.
                    </Typography>
                  )}
                </Grid>
              )}

              {(formData.role === 'CLIENT' || formData.role === 'AGENCY') && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>
                      {formData.role === 'AGENCY' ? 'Connect to Client (Agency)' : 'Connect to Client'}
                    </InputLabel>
                    <Select
                      value={formData.clientId}
                      label={formData.role === 'AGENCY' ? 'Connect to Client (Agency)' : 'Connect to Client'}
                      onChange={(e) => handleChange('clientId', e.target.value)}
                      disabled={loading}
                    >
                      <MenuItem value="">
                        {formData.role === 'AGENCY' ? 'No initial client (create after login)' : 'Select Client'}
                      </MenuItem>
                      {(formData.role === 'AGENCY' ? availableAgencyClients : availableClients).map(
                        (client: Client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name} ({client.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {formData.role === 'CLIENT' && availableClients.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      No clients available for assignment.
                    </Typography>
                  )}
                  {formData.role === 'AGENCY' && availableAgencyClients.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      No unassigned clients available. Agency can still log in and create new clients.
                    </Typography>
                  )}
                  {formData.clientId && (
                    <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                      {formData.role === 'AGENCY'
                        ? '✓ Agency login at /login. Linked client keeps existing projects in the sidebar; the Clients table lists only managed clients.'
                        : '✓ First name and email auto-populated from selected client. Last name is editable.'}
                    </Typography>
                  )}
                  {formData.role === 'AGENCY' && !formData.clientId && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Optional: link an existing client now, or leave empty and let the agency create clients after login.
                    </Typography>
                  )}
                </Grid>
              )}

              {formData.role === 'ADMIN' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    Admin Permissions
                  </Typography>
                  <AdminPermissionsEditor
                    value={adminPermissions}
                    onChange={setAdminPermissions}
                    onOpenClientAssignments={user ? onOpenClientAssignments : undefined}
                    disabled={loading}
                  />
                </Grid>
              )}

              {user && (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleChange('isActive', e.target.checked)}
                        disabled={loading}
                      />
                    }
                    label="Active"
                  />
                </Grid>
              )}

              {user && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Note: Email cannot be changed. To change password, the user should use the change password feature.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : (user ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;
